import { useCallback, useEffect, useRef, useState } from 'react';
import type { AnswerInput } from '../../../api/safetyRounds';
import { saveRoundAnswer } from '../../../api/safetyRounds';
import { EMPTY_QUEUE, enqueue, failed, ready, type SaveQueue, saveState, sending, sent } from './roundView';

const RETRY_MS = 5000;
const TYPING_MS = 800;

/**
 * Saves each answer as soon as it is given. Text is saved when the writer pauses or leaves the
 * field. What fails is tried again every few seconds and when the network comes back.
 */
export const useAnswerAutosave = (roundId: number, save = saveRoundAnswer) => {
  const queue = useRef<SaveQueue>(EMPTY_QUEUE);
  const [state, setState] = useState(saveState(EMPTY_QUEUE));
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const typing = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const retry = useRef<ReturnType<typeof setTimeout> | null>(null);

  const update = (next: SaveQueue) => {
    queue.current = next;
    setState(saveState(next));
  };

  const flush = useCallback(() => {
    if (retry.current) {
      clearTimeout(retry.current);
      retry.current = null;
    }
    for (const itemId of ready(queue.current)) {
      const { queue: next, input } = sending(queue.current, itemId);
      update(next);
      save(roundId, itemId, input)
        .then(({ savedAt }) => {
          setLastSavedAt(savedAt);
          update(sent(queue.current, itemId));
          // A change made while this one was saving
          if (itemId in queue.current.pending) flush();
        })
        .catch(() => {
          update(failed(queue.current, itemId, input));
          if (!retry.current) retry.current = setTimeout(flush, RETRY_MS);
        });
    }
  }, [roundId, save]);

  useEffect(() => {
    window.addEventListener('online', flush);
    return () => {
      window.removeEventListener('online', flush);
      if (retry.current) clearTimeout(retry.current);
      Object.values(typing.current).forEach(clearTimeout);
    };
  }, [flush]);

  /** Saves now, as when a result is chosen or a field is left */
  const saveNow = useCallback(
    (itemId: number, input: AnswerInput) => {
      clearTimeout(typing.current[itemId]);
      update(enqueue(queue.current, itemId, input));
      flush();
    },
    [flush],
  );

  /** Saves when the writer pauses */
  const saveSoon = useCallback(
    (itemId: number, input: AnswerInput) => {
      update(enqueue(queue.current, itemId, input));
      clearTimeout(typing.current[itemId]);
      typing.current[itemId] = setTimeout(flush, TYPING_MS);
    },
    [flush],
  );

  return { state, lastSavedAt, saveNow, saveSoon };
};
